<script lang="ts">
    import {getInjector} from 'src/core';
    import {Kind, SearchType, ViewFormat} from 'shared/constants';
    import type {FieldTypeId, Fond, RecordSearchParams, Sorting, UUID} from 'src/typings';
    import type {DynamicFieldSearchParam, RowCreationParams} from 'src/features/record-grid/lib/types';
    import type {SvelteComponentConstructor} from 'core/types';
    import {RecordGridService} from './services/record-grid.service';
    import {exists} from 'shared/utils/custom-utils';
    import {onMount} from 'svelte';
    import {isEmpty} from 'shared/utils/array-utils';
    import {mapFieldTypeIdToDynamicFieldSearchParamName} from 'shared/utils/record-field-utils';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import KpSearchContext from '../search/kp-search-context/KpSearchContext.svelte';
    import DefaultRowOptionsCell from 'src/features/record-grid/components/cells/DefaultRowOptionsCell.svelte';
    import KpRecordGridDisplay from 'src/features/record-grid/KpRecordGridDisplay.svelte';

    export let fondOrFondId: Fond | number;
    export let dynamicFieldSearchParams: DynamicFieldSearchParam[] = [];
    export let rowCreationParams: RowCreationParams = {initialValues: {}};
    export let referenceRecordId: UUID | null = null;
    export let recordLinkFieldTypeId: FieldTypeId | null = null;
    export let sorting: Sorting | null = null;
    export let qt: string | null = null;
    export let includeAddNewRow = true;
    export let includeToolbar = true;
    export let useGlobalSearch = false;
    export let expandedFirstLevel = false;
    export let stickyHeader = true;
    export let infiniteScroll = false;
    export let pinControlColumn = false;
    export let rowOptionsCellComponent: SvelteComponentConstructor = DefaultRowOptionsCell;
    export let draftsPlacement: 'bottom' | 'top' = 'top';

    const injector = getInjector();
    const recordGridService = injector.getByClass(RecordGridService);

    let selectedFond: Fond;

    onMount(async () => {
        selectedFond = await getFond(fondOrFondId);
    });

    async function loadStaticData(fond: Fond) {
        return Promise.all([
            Promise.resolve(fond),
            recordGridService.loadGridLayout(fond),
            recordGridService.getInsertableFonds(fond)
        ]);
    }

    async function getFond(fondOrId: Fond | number): Promise<Fond> {
        if (typeof fondOrId === 'number') {
            return recordGridService.getFond(fondOrId);
        }

        return fondOrId;
    }

    function createStaticParams(rootFond: Fond): RecordSearchParams {
        return {
            kind: [Kind.KIND_RECORD],
            type: SearchType.TYPE_GLOBAL,
            viewFormat: ViewFormat.TABLE_ROW,
            rootFond: [rootFond],
            pageSize: 50,
            qt: exists(qt) ? qt : undefined,
            sorting: exists(sorting) ? sorting : undefined,
            ...resolveDynamicFieldSearchParam()
        };
    }

    function resolveDynamicFieldSearchParam() {
        if (isEmpty(dynamicFieldSearchParams)) {
            return {};
        }
        const dynamicFieldSearchParam = dynamicFieldSearchParams.at(0);
        return {[mapFieldTypeIdToDynamicFieldSearchParamName(dynamicFieldSearchParam.fieldTypeId, dynamicFieldSearchParam.type)]: dynamicFieldSearchParam.value};
    }

    const handleFondChange = (event: CustomEvent<Fond>) => {
        if (event.detail.id === selectedFond.id) {
            return;
        }

        recordGridService.resetUrlState();
        selectedFond = event.detail;
    };
</script>

{#if !exists(selectedFond)}
    <KpLoadingBlock fillAvailableSpace/>
{/if}

{#if exists(selectedFond)}
    {#key selectedFond}
        {#await loadStaticData(selectedFond)}
            <KpLoadingBlock fillAvailableSpace/>
        {:then [fond, gridLayout, insertableFonds]}
            <KpSearchContext staticParams={createStaticParams(fond)} localSearch={!useGlobalSearch}>
                <KpRecordGridDisplay {referenceRecordId}
                                     {rowCreationParams}
                                     {recordLinkFieldTypeId}
                                     {includeAddNewRow}
                                     {includeToolbar}
                                     {expandedFirstLevel}
                                     {rowOptionsCellComponent}
                                     {stickyHeader}
                                     {infiniteScroll}
                                     {pinControlColumn}
                                     {draftsPlacement}
                                     {fond}
                                     {gridLayout}
                                     {insertableFonds}
                                     on:row-published
                                     on:rows-updated
                                     on:row-deleted
                                     on:fond-change={handleFondChange}/>
            </KpSearchContext>
        {:catch error}
            <div class="text-danger">{JSON.stringify(error, null, 2)}</div>
        {/await}
    {/key}
{/if}
